import {
  ChangeDetectorRef,
  Component,
  Input,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { CustomPieChartData } from 'src/app/impact/models/impactReportData';

interface Label {
  type: string;
  impact: number;
}

@Component({
  selector: 'app-legend-graph',
  templateUrl: './legend-graph.component.html',
  styleUrl: './legend-graph.component.scss',
  standalone: false,
})
export class LegendGraphComponent implements OnChanges {
  @Input() data?: CustomPieChartData;
  @Input() isPercentage?: boolean;
  @Input() overviewMode?: 'detailed' | 'mainVsOnSite';
  @Input() isKilometer?: {
    labels: string[];
    datasets: {
      backgroundColor: string[];
      data: number[];
    }[];
  };

  private cdr = inject(ChangeDetectorRef);
  private transloco = inject(TranslocoService);
  private formatCo2e = inject(FormatCo2eService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.cdr.detectChanges();
    }
  }

  getBackgroundColor(index: number): string {
    const bg = this.data?.chartData?.datasets[0]?.backgroundColor;
    if (Array.isArray(bg)) {
      return bg[index] as string;
    }
    return '#B1B5B1'; // greyscale-400 - couleur de fallback cohérente
  }

  getGroupedLabels(): {
    type: string;
    items: (Label & { occurrence: number })[];
    subcategories?: (Label & { occurrence: number })[];
  }[] {
    const labels = this.getValidLabels();
    const groups: Record<string, (Label & { occurrence: number })[]> = {};
    const subcategories: Record<string, (Label & { occurrence: number })[]> =
      {};

    labels.forEach((label: Label) => {
      if (!groups[label.type]) groups[label.type] = [];
      groups[label.type].push({
        ...label,
        occurrence: groups[label.type].length + 1,
      });

      if (
        (label as Label & { subcategories?: Label[] }).subcategories &&
        Array.isArray(
          (label as Label & { subcategories?: Label[] }).subcategories,
        )
      ) {
        subcategories[label.type] = (
          (label as Label & { subcategories?: Label[] })
            .subcategories as Label[]
        ).map((sub: Label, idx: number) => ({
          ...sub,
          occurrence: idx + 1,
        }));
      }
    });

    return Object.entries(groups).map(([type, items]) => ({
      type,
      items,
      subcategories: subcategories[type],
    }));
  }

  getBackgroundColorForType(type: string): string {
    const labels = this.getValidLabels();
    const index = labels.findIndex((label) => label.type === type);
    return this.getBackgroundColor(index);
  }

  getValidLabels(): Label[] {
    return (this.data?.chartData?.labels || []).filter(
      (label) =>
        label &&
        typeof label === 'object' &&
        'type' in label &&
        'impact' in label,
    ) as Label[];
  }

  getTranslatedLabel(label: Label): string {
    let translated = this.transloco.translate(`categoryType.${label.type}`);
    if (label.type === 'lightHousingYurtTinyHouse') {
      translated = translated + ' ';
    }
    return translated;
  }

  isKilometerValid(): boolean {
    return (
      !!this.isKilometer &&
      Array.isArray(this.isKilometer.datasets) &&
      this.isKilometer.datasets.length > 0 &&
      Array.isArray(this.isKilometer.datasets[0].data)
    );
  }

  getTotalKilometers(): number {
    if (this.isKilometerValid()) {
      return this.isKilometer!.datasets[0].data.reduce(
        (sum, val) => sum + Number(val),
        0,
      );
    }
    return 0;
  }

  getLabelKm(index: number): string {
    if (this.isKilometerValid()) {
      const km = this.isKilometer!.datasets[0].data[index];
      return `${Number(km).toFixed(2)} km`;
    }
    return '- km';
  }

  getKilometerPercentage(index: number): number {
    if (this.isKilometerValid()) {
      const data = this.isKilometer!.datasets[0].data;
      const total = data.reduce((sum, val) => sum + Number(val), 0);
      if (!total) return 0;
      return Math.round((Number(data[index]) / total) * 10000) / 100;
    }
    return 0;
  }

  getLabelTotal(label: Label): string {
    return this.formatCo2e.format(label.impact);
  }

  getGlobalIndex(item: Label & { occurrence: number }): number {
    const labels = this.getValidLabels();
    let occurrence = item.occurrence;
    for (let i = 0; i < labels.length; i++) {
      if (labels[i].type === item.type) {
        occurrence--;
        if (occurrence === 0) {
          return i;
        }
      }
    }
    return -1;
  }

  getSubcategoryPercentage(
    sub: Label,
    group: { subcategories?: Label[] },
  ): number {
    if (!group.subcategories) return 0;
    const total = group.subcategories.reduce(
      (sum: number, s: Label) => sum + s.impact,
      0,
    );
    if (!total) return 0;
    return Math.round((sub.impact / total) * 10000) / 100;
  }

  getPercentage(index: number): number {
    const dataset = this.data?.chartData?.datasets[0];
    if (!dataset || !Array.isArray(dataset.data)) return 0;

    const numericData = dataset.data.map((val) => {
      if (typeof val === 'number') return val;
      if (Array.isArray(val)) return val[1];
      if (
        val &&
        typeof val === 'object' &&
        'y' in val &&
        typeof val.y === 'number'
      )
        return val.y;
      return 0;
    });

    const total = numericData.reduce((sum, val) => sum + val, 0);
    if (!total) return 0;

    const percentages = numericData.map((value) => (value / total) * 100);

    if (index === percentages.length - 1) {
      const roundedTotal = percentages
        .slice(0, -1)
        .reduce((sum, val) => sum + Math.round(val * 100) / 100, 0);
      return Math.round((100 - roundedTotal) * 100) / 100;
    }

    return Math.round(percentages[index] * 100) / 100;
  }
}
